import { z } from 'zod';
import { cardReferenceSchema } from './board';
import { EVENT_TYPE_NAMES, isStorableEventName } from './events';
import { ulidSchema } from './id';

const timestampStringSchema = z.string().min(1).max(64);

export const changeRegistrationSubjectKindSchema = z.enum(['board', 'list', 'card']);
export type ChangeRegistrationSubjectKind = z.infer<
  typeof changeRegistrationSubjectKindSchema
>;

export const changeRegistrationTargetSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('board'), id: ulidSchema }),
  z.strictObject({ kind: z.literal('list'), id: ulidSchema }),
  z.strictObject({ kind: z.literal('card'), id: ulidSchema }),
]);
export type ChangeRegistrationTarget = z.infer<typeof changeRegistrationTargetSchema>;

/**
 * **The READ shape** — what a stored `change_registrations.event_filter` is
 * allowed to deserialise as, on every response that carries one.
 *
 * ⚠ DELIBERATELY THE FULL REGISTRY, AND DELIBERATELY WIDER THAN THE CREATE
 * BODY BELOW. See `changeRegistrationEventFilterInputSchema`. Narrowing this
 * one is the tidy-up that looks obviously right and turns a stored row into a
 * 500 on a route whose job is to show the member what they have.
 */
export const changeRegistrationEventFilterSchema = z.array(z.enum(EVENT_TYPE_NAMES));
export type ChangeRegistrationEventFilter = z.infer<
  typeof changeRegistrationEventFilterSchema
>;

/**
 * **The WRITE shape** — the alphabet `POST /me/change-registrations` accepts,
 * which is `STORABLE_EVENT_TYPE_NAMES`: the registry **minus its
 * placeholders**, 128 names rather than 129.
 *
 * ⚠ WHY THE FILTER ALPHABET IS NARROWER THAN `EVENT_TYPE_NAMES`, SO THE NEXT
 * READER DOES NOT "FIX" THE NARROWING BACK OUT. `skeleton.noop` is in the
 * registry on purpose — it is the socket placeholder
 * `apps/server/src/ws/board-socket.integration.test.ts` publishes to prove the
 * Redis → socket fan-out, and `./events`' header says to keep it — but
 * `ck_activity_entries_type` does not admit it, so **no activity entry can
 * ever carry that name**. The matcher in
 * `apps/server/src/external/change-registrations.ts` joins deliveries to
 * committed entries, so a registration filtered on it matches nothing, for
 * ever, and the member is told nothing: today the route answers `201` and the
 * integration is simply silent. A `422` at the boundary is the honest answer,
 * and it is the same position the `^https?://` refusal on `callbackUrl` below
 * already occupies — `validation_failed` is 422 in `./errors`, which
 * `search-results.integration.test.ts:1845` and
 * `board-background.integration.test.ts:404` both assert of a body/querystring
 * refusal on this handler.
 *
 * ⚠ A SEPARATE CONST, NOT A NARROWING OF THE SCHEMA ABOVE — CLAUDE.md's
 * "extend a shipped schema on a copy, never widen it in place", applied in the
 * narrowing direction, and the copy is load-bearing.
 * `changeRegistrationEventFilterSchema` serialises `POST … 201`
 * (`changeRegistrationSchema`), `DELETE … 200` and, through
 * `changeRegistrationWithAccessSchema`, `GET /me/change-registrations`.
 * `ck_change_registrations_event_filter_array` checks only
 * `jsonb_typeof = 'array'` — it validates no names — so a row containing
 * `skeleton.noop` **is storable** and may predate this refusal. Narrowing the
 * read path would turn that harmless no-op row into a 500 on the one route
 * that exists to show a member their registrations: strictly worse than the
 * thing being fixed. Measured at the time of writing: 0 such rows locally, and
 * production was **not** measured — which is the whole reason the read path
 * stays wide.
 *
 * ⚠ THE `skeleton.noop`-IS-STILL-ACCEPTED-ON-READ ASSERTION IN
 * `./change-registration.test.ts` IS NOT REDUNDANT: it is what pins this split
 * open. Collapse the two consts and only that test goes red.
 *
 * ⚠ A RUNTIME REFUSAL OVER THE FULL ENUM, NOT `z.enum(STORABLE_EVENT_TYPE_NAMES)`,
 * AND THE DIFFERENCE IS MEASURED RATHER THAN STYLISTIC. The narrow enum is the
 * obvious spelling and it makes this schema's **output type**
 * `StorableEventType[]`, which is narrower than
 * `ChangeRegistrationEventFilter`. `ChangeRegistrationManager.tsx:359` feeds a
 * value of the *wide* type straight into this body, so the narrow enum fails
 * `apps/web`'s `tsc` with `TS2345 … Type '"skeleton.noop"' is not
 * assignable …` — and that file is owned by another branch and may not be
 * edited from here. `.refine` keeps the output type identical to the read
 * shape and moves only the *check*, which is all this defect needs: every
 * property the split exists for survives, and no consumer's types move.
 * `STORABLE_EVENT_TYPE_NAMES` is exported beside the guard regardless, because
 * it is exactly the list a curated event picker needs.
 *
 * ⚠ KNOWN, NAMED CONSEQUENCE, LEFT FOR ITS OWNER — AND IT IS **TWO** SITES IN
 * `apps/web`, NOT ONE. `ChangeRegistrationManager.tsx` builds its free-text
 * validation set from the **full** `EVENT_TYPE_NAMES`, so it still accepts
 * `skeleton.noop` inline; the request then **never reaches the route**,
 * because `apps/web/src/lib/change-registrations.ts:31` runs
 * `createChangeRegistrationBodySchema.parse(body)` before the POST and now
 * throws a `ZodError` client-side, which react-query surfaces as a mutation
 * error. So the 422 above is what a direct `POST` sees — a connected app,
 * `curl`, the integration suite — while the UI fails one step earlier with a
 * less specific message. Both are loud, and both beat today's silent `201`,
 * which is why this is recorded rather than repaired here: the block replacing
 * the free-text field with a curated picker owns both files, and
 * `STORABLE_EVENT_TYPE_NAMES` is exactly the list that picker needs.
 *
 * ⚠ ONE COST OF THE `.refine` SPELLING, RECORDED SO IT IS NOT DISCOVERED
 * TWICE: a `.refine` is invisible to JSON Schema generation, so the
 * `@fastify/swagger` document still advertises all 129 enum values for this
 * field, `skeleton.noop` included, where `z.enum(STORABLE_EVENT_TYPE_NAMES)`
 * would have published 128. Nothing snapshots that document, so nothing is
 * red. It is not a reason to switch back — the narrow enum does not compile,
 * see above — but a generated client will offer a name the route refuses.
 */
export const changeRegistrationEventFilterInputSchema = z.array(
  // ⚠ THE WRAPPER IS NOT NOISE — `.refine(isStorableEventName, …)` DOES NOT
  // COMPILE TO THE SAME THING. Zod v4 has a type-predicate overload of
  // `refine`, so passing the guard directly narrows this schema's OUTPUT to
  // `StorableEventType[]` — reintroducing, through the back door, the exact
  // `TS2345` in `ChangeRegistrationManager.tsx:359` that this spelling exists
  // to avoid. Measured: it failed `apps/web`'s `tsc` identically. The arrow
  // returns a plain `boolean`, which selects the non-narrowing overload and
  // keeps the check at runtime where it belongs.
  z.enum(EVENT_TYPE_NAMES).refine((name): boolean => isStorableEventName(name), {
    message:
      'Event filter names a placeholder event no activity entry can carry, so the registration would never deliver',
  }),
);
export type ChangeRegistrationEventFilterInput = z.infer<
  typeof changeRegistrationEventFilterInputSchema
>;

/**
 * The sentence `webhook-delivery.ts` writes to `change_registrations.last_error`
 * when the sweep discovers that the authorising member has lost access to a
 * registration's subject — `FR-AUTO-034`, "A registration stops delivering when
 * the authorising member loses access to its subject."
 *
 * ⚠ THIS IS MEMBER-FACING COPY, NOT A LOG LINE. `lastError` below is
 * serialised on all three `/me/change-registrations` routes and
 * `ChangeRegistrationManager.tsx` renders it **raw** into a `<Notice>`. Before
 * this constant the branch wrote `errorSummary(error)` — the `ApiError`
 * message thrown by `requireBoardReader`/`requireCardReader`, i.e. the member
 * was shown the string `No such board` as the explanation for why their
 * integration stopped.
 *
 * ⚠ NOT the same string as `markSkipped`'s
 * `'registration no longer has access to its target'`, which goes to
 * `webhook_deliveries.last_error` — an internal row nothing serialises to a
 * member. Two audiences, two strings, deliberately; the vocabulary
 * ("change registration") is one.
 */
export const CHANGE_REGISTRATION_ACCESS_LOST_ERROR =
  'Delivery stopped because you no longer have access to this registration’s subject.';

/**
 * ⚠ **NO SECOND MEMBER-FACING CONSTANT IS ADDED BESIDE THE ONE ABOVE, AND THE
 * ABSENCE IS A DECISION.** Signing and the failure alert each look like they
 * want one, and neither does. Recorded here because the next reader's first
 * instinct is to mint the pair, and because the three strings they would be
 * duplicating each already live somewhere with a better claim on them:
 *
 *   - **The failure alert's sentence** is a `notifications` row of type
 *     `change_registration_failing`, and its wording lives in
 *     `NOTIFICATION_INTERFACE_TEMPLATES` (`./interface-language`) like every
 *     other notification's. `.b28-design.md` §3b's ruling — a *rendered*
 *     English string must not go on the wire, because `FR-COLLAB-018` wants
 *     "translatable sentence templates rather than string concatenation" —
 *     applies to it exactly as it applies to `mentioned`. A constant here
 *     would be that concatenation shipped one layer early, and it would be
 *     untranslatable for the one surface (email) that has no client to render
 *     it.
 *   - **"This registration has no signing secret"** is client copy for a
 *     `hasSigningSecret: false` row (see `changeRegistrationWithAccessSchema`),
 *     so it belongs to `apps/web`'s catalogue, not to a contract the server
 *     writes into a column.
 *   - **A re-enable does not write copy at all.** Clearing `last_error` back
 *     to `null` is the honest record of "this is no longer failing"; leaving a
 *     stale sentence behind, or replacing it with a cheerful one, would both
 *     be a surface asserting something the row does not know.
 *
 * The constant above exists because it is **stored** in a column that three
 * routes serialise and a client renders raw. That is the test a new constant
 * has to pass.
 */

// ---------------------------------------------------------------------------
// Signature transport — the four literals the signing scheme is spelled in.
// ---------------------------------------------------------------------------

/**
 * **One literal, three consumers.** The delivery worker
 * (`apps/server/src/external/webhook-delivery.ts`) writes these headers, the
 * integration tests read them back, and the outward-facing integration guide
 * quotes them at subscribers. Spelled three times they drift, and the drift is
 * silent in the worst direction: a subscriber verifying
 * `X-Molt-Signature` while the sender writes `x-molt-signature-v1` sees no
 * signature, and the honest implementation of "reject unsigned deliveries" is
 * then to reject everything.
 *
 * ⚠ **Lower case, because that is what a reader gets back.** `fetch`'s
 * `Headers` normalises names to lower case and Node's HTTP server does the
 * same, so a subscriber comparing against a `X-Molt-…` literal never matches.
 * The names are written here in the form every consumer actually observes.
 */
export const CHANGE_REGISTRATION_SIGNATURE_HEADER = 'x-molt-signature';

/**
 * The `webhook_deliveries.id` of this attempt — `deliveryId` in the body too,
 * and **stable across retries**: the row is unique per
 * `(change_registration_id, activity_entry_id)`
 * (`uq_webhook_deliveries_registration_activity`), so attempt 1 and attempt 5
 * carry the same value. That is what makes it usable as an idempotency key by
 * a subscriber, which is the only reason it is worth a header as well as a
 * body field: a subscriber can dedupe before parsing.
 */
export const CHANGE_REGISTRATION_DELIVERY_ID_HEADER = 'x-molt-delivery-id';

/**
 * The `activity_entries.type` this delivery is about — the same value as
 * `activityEntry.type` in the body, in the dotted event spelling.
 *
 * ⚠ **A convenience for routing, NOT a thing to authenticate on.** It is
 * outside the signed material (only `${t}.${rawBody}` is signed), so a
 * subscriber that branches on this header before verifying is branching on an
 * attacker-chosen string. Read the body's `activityEntry.type` for anything
 * that matters.
 */
export const CHANGE_REGISTRATION_EVENT_HEADER = 'x-molt-event';

/**
 * The scheme version token, which appears in the header value as `v1=<hex>`.
 *
 * Versioned from the first delivery so that a `v2` can be added **beside**
 * `v1` in one header value rather than replacing it — the migration path that
 * does not break every shipped subscriber on the day the scheme changes.
 */
export const CHANGE_REGISTRATION_SIGNATURE_SCHEME = 'v1';

/**
 * How many random bytes a signing secret is, and how many characters that is
 * in lower-case hex. 32 bytes is the HMAC-SHA256 block-aligned key size, so
 * the key is neither padded nor hashed down before use.
 *
 * ⚠ **`ck_change_registrations_signing_secret` is the other half of this
 * agreement** — `0062_webhook_signing_and_failure_notifications.sql` CHECKs
 * `^[0-9a-f]{64}$` on the column. Generate with
 * `randomBytes(CHANGE_REGISTRATION_SIGNING_SECRET_BYTES).toString('hex')` in
 * the route; `node:crypto` deliberately does not reach this package (see
 * `changeRegistrationSignedMaterial`), and `pgcrypto` is not installed on this
 * database, so SQL cannot mint one either.
 */
export const CHANGE_REGISTRATION_SIGNING_SECRET_BYTES = 32;
export const CHANGE_REGISTRATION_SIGNING_SECRET_HEX_LENGTH = 64;

/**
 * **EXACTLY WHAT IS SIGNED**, as code rather than as prose, because prose is
 * what two implementations of a signature disagree about.
 *
 *     signed bytes  =  `${unixSeconds}.${rawBody}`
 *     header value  =  `t=${unixSeconds},v1=${hex hmac-sha256}`
 *     key           =  the registration's `signing_secret`, hex, 32 bytes
 *
 * ⚠ **THE TIMESTAMP IS INSIDE THE SIGNED MATERIAL, AND THAT IS THE WHOLE
 * POINT OF IT.** A `t=` a verifier reads but does not authenticate is
 * forgeable, so an attacker replaying a captured body simply rewrites `t` to
 * now and the freshness check passes — the header would then buy nothing while
 * looking like replay protection. Because `t` is signed, changing it
 * invalidates the MAC, so a subscriber that both verifies the MAC and bounds
 * `t` has a real window.
 *
 * ⚠ **`rawBody` IS THE BYTES ON THE WIRE, NOT A RE-SERIALISATION.** The sender
 * signs the exact string it hands the transport, and a subscriber must verify
 * against the exact string it received — `JSON.parse` then `JSON.stringify`
 * reorders nothing today and will reorder something eventually, and the
 * failure is a signature mismatch on a body that is otherwise perfect. Say so
 * in the integration guide; it is the defect subscribers hit first.
 *
 * ⚠ **NO HMAC HERE, DELIBERATELY.** `packages/shared` is imported by
 * `apps/web` and must stay free of `node:crypto` — importing it would break
 * the browser bundle, and a Web Crypto fallback would put a second
 * implementation of the signature in the product. This package owns the
 * *format*; the server owns the *computation*.
 */
export function changeRegistrationSignedMaterial(
  unixSeconds: number,
  rawBody: string,
): string {
  return `${unixSeconds}.${rawBody}`;
}

/**
 * The `CHANGE_REGISTRATION_SIGNATURE_HEADER` value for a computed MAC.
 *
 * `hexMac` is the lower-case hex digest of
 * `changeRegistrationSignedMaterial(unixSeconds, rawBody)` under the
 * registration's secret — the same `unixSeconds` in both calls, which is why
 * the two functions take it rather than reading a clock: a helper that called
 * `Date.now()` twice could sign one second and advertise another, and the
 * failure would be an intermittent signature mismatch nobody can reproduce.
 */
export function changeRegistrationSignatureHeaderValue(
  unixSeconds: number,
  hexMac: string,
): string {
  return `t=${unixSeconds},${CHANGE_REGISTRATION_SIGNATURE_SCHEME}=${hexMac}`;
}

export const changeRegistrationSchema = z.object({
  id: ulidSchema,
  connectedAppId: ulidSchema,
  target: changeRegistrationTargetSchema,
  callbackUrl: z.url(),
  eventFilter: changeRegistrationEventFilterSchema,
  active: z.boolean(),
  failing: z.boolean(),
  failureCount: z.number().int().min(0),
  lastDeliveryAt: timestampStringSchema.nullable(),
  lastStatusCode: z.number().int().min(100).max(599).nullable(),
  lastError: z.string().nullable(),
  nextRetryAt: timestampStringSchema.nullable(),
  createdAt: timestampStringSchema,
  updatedAt: timestampStringSchema,
});
export type ChangeRegistration = z.infer<typeof changeRegistrationSchema>;

/**
 * `changeRegistrationSchema` **plus** the read-path access re-check —
 * `FR-AUTO-034`, "A registration stops delivering when the authorising member
 * loses access to its subject."
 *
 * ⚠ THIS IS A COPY, NOT A WIDENING, AND THE COPY IS LOAD-BEARING. One schema
 * serialised all three `/me/change-registrations` routes; only the LIST route
 * can honestly answer this question. Measured: `changeRegistrationSchema`
 * serialises `POST … 201` and `DELETE … 200`;
 * `changeRegistrationListSchema` serialises `GET /me/change-registrations`
 * and nothing else in the server. Extending in place would have put a field on
 * the two write responses that never evaluate access — so "POST and DELETE do
 * not carry `subjectAccessible`" is a guarantee of the SCHEMA here, not of
 * handler discipline.
 *
 * ⚠ OPTIONAL, DELIBERATELY, AND FORCED BY A FILE THIS CONTRACT DOES NOT OWN.
 * `apps/web/src/lib/change-registrations.ts` splices an ITEM-schema value (the
 * `POST`/`DELETE` result) into a LIST-schema array on cache upsert. A required
 * field would stop that splice compiling and push the repair into a file
 * another chunk owns. Three states, and the third is the point:
 *
 *   `undefined` — this response did not evaluate access (a row spliced into the
 *                 cache from a `POST` or `DELETE` result)
 *   `true`      — the member can currently read the subject
 *   `false`     — the member cannot; the delivery path will refuse
 *
 * ⚠ `true` IS NOT A PROMISE THAT THE NEXT DELIVERY SUCCEEDS. It answers only
 * `FR-AUTO-034`'s own question — can the authorising member read the subject —
 * and deliberately folds in neither the grant's revocation/expiry (already
 * surfaced beside it as `app.revoked`) nor the grant's own board scope.
 * `false`, however, IS decisive.
 *
 * Why the field exists at all: `active` is stored, and the only thing that ever
 * writes `active = false` for lost access is the delivery sweep, which runs
 * only when a commit on the subject has queued a delivery to attempt. On a
 * quiet board nothing is queued, so a registration whose member lost access
 * reads `active: true` for ever and the surface calls it healthy. This field is
 * the discoverer that does not depend on there being traffic. It is transient:
 * `active` is still returned AS STORED and no read mutates it.
 */
/**
 * `changeRegistrationSchema` **plus the signing secret** — the shape for the
 * **create** response and the **rotate** response, and for nothing else, ever.
 *
 * ⚠ A COPY, NOT A WIDENING, ON `changeRegistrationWithAccessSchema`'s OWN
 * ARGUMENT RUN THE OTHER WAY. That docstring's measurement still holds and is
 * the reason this file needs a third schema rather than a field:
 * `changeRegistrationSchema` serialised `POST … 201` **and** `DELETE … 200`
 * when this schema was declared, and `changeRegistrationWithAccessSchema` (through
 * `changeRegistrationListSchema`) serialises `GET /me/change-registrations`.
 * Putting `signingSecret` on the base schema would therefore put the secret in
 * the `DELETE` response as well as the `POST` one; putting it on the list copy
 * would return every registration's secret on every drawer open, for ever.
 * **"GET and DELETE cannot carry the secret" is a guarantee of the SCHEMA
 * here, not of handler discipline** — a handler that read the column and
 * spread the row into a `changeRegistrationSchema` response would have the
 * field stripped by `z.object`, which is the one place stripping is the
 * behaviour you want.
 *
 * ⚠ **SHOWN ONCE, AND THE ROUTES LANE MUST NOT ADD A READ.** The secret is
 * displayed at creation and at rotation because those are the two moments the
 * member is standing in front of the value and can copy it. A `GET` that
 * returned it would make every session, every cached response and every
 * browser history entry a copy of a signing key, and it would make
 * `hasSigningSecret` below pointless — that boolean exists precisely so the
 * surface can talk about the secret without transporting it.
 *
 * ⚠ **EXACTLY TWO ROUTES SERIALISE THIS SCHEMA, AND NO OTHER ROUTE MAY.**
 * Measured on the merged tree: `apps/server/src/routes/change-registrations.ts`
 * has six routes, and this schema is the `response` of two of them — `POST
 * /me/change-registrations` (`201`) and `POST
 * /me/change-registrations/:id/rotate-signing-secret` (`200`). `GET` serialises
 * `changeRegistrationListSchema`; `POST …/resume`, `PATCH …/event-filter` and
 * `DELETE` serialise the base `changeRegistrationSchema`, which carries neither
 * the secret nor `hasSigningSecret`. Re-measure with
 * `grep -n "response: {" apps/server/src/routes/change-registrations.ts` rather
 * than quoting the count.
 */
export const changeRegistrationWithSecretSchema = changeRegistrationSchema.extend({
  /**
   * The registration's `signing_secret`, lower-case hex, 32 bytes — the key a
   * subscriber verifies `CHANGE_REGISTRATION_SIGNATURE_HEADER` with.
   *
   * ⚠ **Required, and not nullable, on this schema alone.** A create or a
   * rotate that could answer `signingSecret: null` would be a response the
   * member cannot act on and cannot distinguish from a bug, so the absence of
   * a nullable arm here is what forces the route to mint the value before it
   * answers. Registrations that predate signing genuinely have no secret —
   * that state is expressible, but on the LIST copy and as a boolean, never as
   * a null here.
   */
  signingSecret: z
    .string()
    .length(CHANGE_REGISTRATION_SIGNING_SECRET_HEX_LENGTH)
    .regex(/^[0-9a-f]+$/, {
      message: 'Signing secret must be lower-case hex',
    }),
});
export type ChangeRegistrationWithSecret = z.infer<
  typeof changeRegistrationWithSecretSchema
>;

export const changeRegistrationWithAccessSchema = changeRegistrationSchema.extend({
  subjectAccessible: z.boolean().optional(),
  /**
   * Whether this registration has a signing secret configured at all —
   * **never the secret itself**.
   *
   * Why it is needed: signing arrived after registrations existed, and
   * `0062`'s column is nullable and **not backfilled**, so a registration
   * created before it delivers unsigned for ever until somebody rotates a
   * secret onto it. Without this field the surface cannot tell that member
   * anything: `active: true`, `failing: false`, deliveries succeeding, and a
   * subscriber verifying signatures that are not being sent. The honest
   * reading of a `false` here is *"this integration's deliveries are not
   * signed"*, which is a sentence the member can act on.
   *
   * ⚠ **`.optional()` FOR `subjectAccessible`'s MEASURED REASON, NOT FOR
   * SYMMETRY.** `apps/web/src/lib/change-registrations.ts` splices an
   * ITEM-schema value (the `POST`/`DELETE` result) into a LIST-schema array on
   * cache upsert; a required field would stop that splice compiling and push
   * the repair into a file another lane owns. Three states, and the third is
   * again the point:
   *
   *   `undefined` — this response did not answer the question (a row spliced
   *                 into the cache from a `POST` or `DELETE` result)
   *   `true`      — a secret is configured; deliveries are signed
   *   `false`     — no secret; deliveries are unsigned
   *
   * ⚠ **`true` IS NOT A PROMISE THAT A SIGNATURE VERIFIES.** It answers only
   * "is the column non-null". Whether the subscriber holds the *same* secret
   * is unknowable from this side — a rotation the member never copied over
   * reads `true` here and fails at every subscriber.
   */
  hasSigningSecret: z.boolean().optional(),
});
export type ChangeRegistrationWithAccess = z.infer<
  typeof changeRegistrationWithAccessSchema
>;

export const changeRegistrationListSchema = z.object({
  changeRegistrations: z.array(changeRegistrationWithAccessSchema),
});
export type ChangeRegistrationList = z.infer<typeof changeRegistrationListSchema>;

export const createChangeRegistrationBodySchema = z.strictObject({
  connectedAppId: ulidSchema,
  target: changeRegistrationTargetSchema,
  callbackUrl: z.url().regex(/^https?:\/\//, {
    message: 'Callback URL must use http or https',
  }),
  // ⚠ THE INPUT SCHEMA, NOT THE READ ONE — 128 names, not 129. See
  // `changeRegistrationEventFilterInputSchema`. Still `.optional()`: an absent
  // or empty filter means "every event on the subject", which is shipped
  // fail-open behaviour and is not what this refusal is about.
  eventFilter: changeRegistrationEventFilterInputSchema.optional(),
});
export type CreateChangeRegistrationBody = z.infer<
  typeof createChangeRegistrationBodySchema
>;

/**
 * The body of the field-scoped patch that **replaces** a registration's event
 * filter — `SC-4`, one route per field, so this is
 * `PATCH /me/change-registrations/:id/event-filter` and not an object `PUT`
 * that could also move the callback URL or the target.
 *
 * ⚠ **THE WRITE ALPHABET, NOT THE READ ONE — 128 names, not 129.** It reuses
 * `changeRegistrationEventFilterInputSchema` for every reason that schema's
 * own docstring gives at length: a filter naming `skeleton.noop` matches
 * nothing for ever and tells the member nothing, and 422 at the boundary is
 * the honest answer. Pointing this body at `changeRegistrationEventFilterSchema`
 * (the wide read shape) would reopen exactly that silence on the one route
 * whose entire job is to change the filter — the tidy-up that looks obviously
 * right, made here rather than at create time.
 *
 * ⚠ **REQUIRED, unlike the same field on `createChangeRegistrationBodySchema`,
 * and the asymmetry is the semantics rather than an oversight.** On create,
 * absent means "every event on the subject" (shipped fail-open behaviour). On
 * a replace, absent would mean *nothing at all* — a route asked to replace a
 * value with no value, which can only be a caller bug, and a 422 naming the
 * missing field beats a 200 that did nothing. **An empty ARRAY is still legal
 * and still means "every event"**: that is how a member widens a narrowed
 * registration back out, and it is a different request from sending no field.
 */
export const replaceChangeRegistrationEventFilterBodySchema = z.strictObject({
  eventFilter: changeRegistrationEventFilterInputSchema,
});
export type ReplaceChangeRegistrationEventFilterBody = z.infer<
  typeof replaceChangeRegistrationEventFilterBodySchema
>;

/**
 * The card a card-scoped delivery is about, with the two facts subscribers
 * asked for that `activity_entries.payload` does not carry.
 */
export const webhookDeliveryCardContextSchema = z.object({
  id: ulidSchema,
  /**
   * Who created the card, or `null` — **best-effort, and the nullability is
   * structural rather than defensive.**
   *
   * ⚠ **`cards` HAS NO CREATOR COLUMN.** Measured, not assumed: there is no
   * `created_by_user_id` on that table and never has been. The only derivation
   * available is the actor of the card's earliest creation-family activity
   * entry — `card.created`, `card.created_by_email`,
   * `card.created_from_template`, `card.copied_from_card` and
   * `card.link_card_created`, and **not** `card.mirror_created`, which
   * `ck_activity_entries_type` admits and which emits the *source* card's id,
   * so admitting it would answer "who mirrored this card". The set and that
   * argument live at `CARD_CREATION_EVENT_TYPES` in
   * `apps/server/src/external/webhook-delivery.ts`; do not widen it from here.
   * And `activity_entries.actor_user_id` **is nullable** — `0004` permits a null
   * actor on an entry the system authored rather than a person, so a card
   * created by an automation rule, by inbound email, or by any other unattended
   * path resolves to `null` here truthfully.
   *
   * ⚠ **Do not "fix" this to non-nullable by falling back to the delivery's own
   * actor.** That would answer "who touched it last" while the field says "who
   * created it", which is the kind of wrong a subscriber cannot detect. `null`
   * means *the product does not know*, and that is a sentence a subscriber can
   * handle; a plausible wrong id is not.
   *
   * ⚠ And it can be null for a second reason: nothing deletes activity
   * entries, but a card imported, merged or mirrored may have no creation-family
   * entry on **this** board at all.
   */
  creatorUserId: ulidSchema.nullable(),
  /**
   * The card's members, as user ids, resolved from `card_members` at delivery
   * time — see `resolvedAt` on the enclosing object before using this for
   * anything time-sensitive.
   *
   * Empty array, never `null`: "no members" is a fact, and a subscriber
   * iterating an array needs no null branch to express it.
   */
  memberUserIds: z.array(ulidSchema),
  /**
   * The card's reference (`MG-0001`) **as the authorising member may use it**,
   * resolved at delivery time like everything else here — so a key change or a
   * cross-board move between the event and the attempt shows the current one.
   * `docs/notes/card-keys.md`.
   *
   * ⚠ Nullable, and `null` is ordinary: a personal-inbox card has no board and
   * no number, a deleted card has nothing to resolve, and a member who reads
   * the board only because it is public may not resolve references on it
   * (`isBoardReferenceableBy` — a reference is enumerable). In every one of
   * those cases `id` is still the card, and `GET /cards/:id` still reads it.
   */
  ref: cardReferenceSchema.nullable(),
});
export type WebhookDeliveryCardContext = z.infer<typeof webhookDeliveryCardContextSchema>;

/**
 * **The block that makes card members, the card's creator and the mention set
 * reachable from a delivery — `FR-AUTO-034`.**
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ DECLARING IT HERE IS WHAT MAKES THE FEATURE POSSIBLE AT ALL
 * ---------------------------------------------------------------------------
 * `deliveryPayload()` in `apps/server/src/external/webhook-delivery.ts`
 * returns `webhookDeliveryPayloadSchema.parse(...)`, and **`z.object` STRIPS
 * unknown keys**. So a delivery worker that resolves a perfect context and
 * spreads it into that object, without this declaration, produces a body with
 * no context in it — a green suite, a 200 from the subscriber, and a field
 * that silently does not exist. The schema is not documentation of the body
 * here; it is the body's filter.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `resolvedAt` IS LOAD-BEARING: THIS IS DELIVERY-TIME STATE, NOT
 * EVENT-TIME STATE
 * ---------------------------------------------------------------------------
 * Everything under `activityEntry` is what happened, frozen when the
 * transaction committed. Everything under `context` is read when the delivery
 * is **attempted**, which is a different moment and can be a much later one:
 * retry backoff runs to roughly fifteen minutes, so the member list attached
 * to attempt 5 is the list as it stands then, not the list at the moment of
 * the change. A subscriber reconstructing history from these bodies will get
 * the wrong answer unless it knows which timestamp each half belongs to, and
 * `resolvedAt` is how it knows.
 *
 * ⚠ It also means the two halves can **contradict** each other legitimately:
 * a `member.removed` event whose `context.card.memberUserIds` still contains
 * the removed user (they were re-added during the backoff), or an event on a
 * card whose members have all changed since. Neither is a bug. Resolving the
 * context at event time instead is not the fix — it would need a second write
 * inside `withTransaction` on the mutation path, which is a cost every
 * mutation pays for a feature only subscribers use.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `mentionedUserIds` DOES NOT REVERSE THE `generate.ts` RULING
 * ---------------------------------------------------------------------------
 * `apps/server/src/notifications/generate.ts` (around `:815`) refused to carry
 * the resolved mention set in the **event payload**, and that ruling stands
 * untouched: **nothing is written to `activity_entries` by this field**, the
 * event vocabulary is not widened, and no migration touches
 * `ck_activity_entries_type`. The set is read at delivery from the
 * `notifications` rows the product had already computed for its own bell —
 * `type = 'mentioned'` joined on `activity_entry_id = ` the event's id — so it
 * inherits the permission scoping those rows were generated under rather than
 * re-deriving it. A mention of somebody who cannot read the board produces no
 * notification row and therefore appears in no delivery, which is the correct
 * answer and one a fresh derivation from the comment body would get wrong.
 *
 * ⚠ **`notifications.activity_entry_id` is not formally `NOT NULL`, and the
 * join is still sound.** Measured in `molt_v1_probe`: 8574 rows of
 * `type='mentioned'`, 0 with a null `activity_entry_id`, and `generate.ts`'s
 * `buildRow` sets it to `event.id` under a stated never-null invariant. The
 * nulls that column legitimately holds belong to the sweep-written types
 * (`due_soon`, and now `change_registration_failing`), which are not
 * `mentioned` and cannot join.
 *
 * ⚠ **ON `comment.edited` THIS IS THE NEWLY-MENTIONED USERS ONLY, NOT EVERY
 * MENTION IN THE COMMENT.** `generate.ts` dedupes mention notifications on
 * `(recipient, comment)` with a `NOT EXISTS`, so editing a comment to add one
 * name writes one row and the rest of the names produce nothing. A subscriber
 * treating this field as "the comment's mentions" will see them vanish on
 * edit; it is "who was newly told", which is a different and narrower fact.
 * Say so in the integration guide rather than papering over it here — the
 * alternative is re-parsing the comment body, which is the derivation the
 * paragraph above explains is wrong.
 *
 * ⚠ Empty on every event that is not a comment, and empty rather than absent.
 */
export const webhookDeliveryContextSchema = z.object({
  /**
   * The card this delivery is about, or `null` when the event is not
   * card-scoped (a board or list event, `activityEntry.cardId === null`).
   *
   * Nullable rather than optional: "this event has no card" is a fact worth
   * transporting, and a subscriber reading `context.card === null` needs no
   * key-existence check to get it right.
   */
  card: webhookDeliveryCardContextSchema.nullable(),
  mentionedUserIds: z.array(ulidSchema),
  /**
   * **The member whose grant authorises this registration** —
   * `change_registrations.connected_app_id -> connected_apps.user_id`, the
   * same resolution `assertCurrentAccess` and the failing-registration
   * notification both make.
   *
   * ⚠⚠ **IT IS WHAT MAKES CLIENT-SIDE SELF-FILTERING POSSIBLE AT ALL, AND
   * WITHOUT IT A SUBSCRIBER CANNOT TELL ITS OWN WRITES FROM A HUMAN'S.** An
   * integration that both reads and writes through molt sees its own changes
   * come back as deliveries, so it needs to drop them or it loops. It cannot:
   * `changeRegistrationEventFilterSchema` filters on the **event name** and
   * nothing else — there is no actor exclusion in the matcher — and a write
   * made with a connected-app bearer token records the **grant owner's**
   * `actor_user_id` on the activity entry, byte-identical to what the same
   * person clicking in the board would have recorded. So
   * `activityEntry.actorUserId` alone cannot separate "my app did this" from
   * "my user did this by hand", and a subscriber comparing it against a user
   * id it happens to know is comparing against a value it had to be told out
   * of band. This field is that value, on the wire, per delivery.
   *
   * ⚠ **A COMPARISON KEY, NOT A LICENCE.** `actorUserId === authorisingUserId`
   * means *this change was made by the identity your registration runs as* —
   * which covers the app's own writes and that member's own gestures
   * together, and cannot distinguish them. A subscriber that must tell those
   * two apart has to carry its own idempotency key through its writes; this
   * field is the cheap filter, not a complete one. Say so in the integration
   * guide.
   *
   * ⚠ Nullable, because the context as a whole is resolved at delivery and
   * `null` is this block's word for *the product could not resolve it for this
   * delivery* — a grant revoked and swept away between enqueue and attempt
   * resolves here truthfully rather than guessing. `null` is never a licence
   * to fall back to `actorUserId`: that would answer "who touched it" while
   * the field says "who authorised the subscription".
   */
  authorisingUserId: ulidSchema.nullable(),
  /**
   * When the fields above were read — **the delivery attempt's clock, not the
   * event's**. See this object's docstring; it is the field that makes the
   * two-timestamp shape of the body legible instead of misleading.
   *
   * ⚠ `timestampStringSchema`, like every timestamp in this package, and
   * emphatically **not** `z.iso.datetime()`: `pg` hands a `timestamptz` back
   * in Postgres' own spelling (a space rather than a `T`, an offset rather
   * than a `Z`), which is a legitimate instant and not an ISO 8601 form. The
   * failure would be a throw inside `deliveryPayload` on every delivery.
   */
  resolvedAt: timestampStringSchema,
});
export type WebhookDeliveryContext = z.infer<typeof webhookDeliveryContextSchema>;

export const webhookDeliveryPayloadSchema = z.object({
  deliveryId: ulidSchema,
  registrationId: ulidSchema,
  activityEntry: z.object({
    id: ulidSchema,
    type: z.enum(EVENT_TYPE_NAMES),
    boardId: ulidSchema.nullable(),
    cardId: ulidSchema.nullable(),
    actorUserId: ulidSchema.nullable(),
    actorAutomationName: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
    createdAt: timestampStringSchema,
  }),
  /**
   * ⚠ **REQUIRED, NOT OPTIONAL, AND THE CHOICE IS THE WHOLE POINT.** The
   * instruction this field was declared under is "make the choice that cannot
   * silently produce a body without it", and only `required` has that
   * property: `.optional()` lets a delivery worker that forgets to resolve the
   * context ship a body with no `context` key at all, indistinguishable from a
   * subscriber's own parsing bug, for ever. Required means
   * `webhookDeliveryPayloadSchema.parse` **throws** instead — loudly, on the
   * first delivery, naming the missing path.
   *
   * ⚠⚠ **AND THE THROW IS NOT CONTAINED, WHICH THE DELIVERY LANE MUST KNOW
   * BEFORE IT SHIPS.** Measured in
   * `apps/server/src/external/webhook-delivery.ts`: `deliveryPayload(row)` is
   * called at the `const body = JSON.stringify(...)` line, which sits
   * **outside** the `try` that wraps `fetchImpl`, and `deliver()` has no outer
   * guard; `sweepWebhookDeliveries` then iterates rows with a bare `await
   * deliver(row, fetch)` and no per-row `catch`. So a `ZodError` here aborts
   * the **whole sweep**, leaving every row it had already claimed locked and
   * unattempted — including, in `molt_v1_probe`, sibling worktrees' rows. The
   * field is required so that this cannot happen quietly.
   *
   * ⚠ **So the lane that declared this field also shipped the producer, in
   * the same commit** — `deliveryPayload()`'s object literal is TYPE-ANNOTATED
   * so that the next required field added here is a compile error there rather
   * than a throw on the first delivery. Declaring this field without touching
   * the producer is what the first draft of that commit did: `pnpm typecheck`,
   * lint and every unit suite stayed green (the only test that calls
   * `deliveryPayload` is in the integration suite) while the sweep would have
   * died on its first claimed row and re-died on every re-claim — never
   * reaching `markFailed`, so `attempt_count` never rose, `failing` never
   * flipped, the retry cap disabled nothing, and the
   * `change_registration_failing` alert this work exists to enable could never
   * fire either. Zero deliveries would have left the product, with nothing red.
   *
   * ⚠ **THE PLACEHOLDER IS GONE, AND THIS PARAGRAPH IS WHERE ITS NAME SURVIVES
   * ON PURPOSE.** The producer's first draft built this block from an
   * `unresolvedDeliveryContext()` that answered `card: null` and
   * `mentionedUserIds: []` on every event, card-scoped ones included. The
   * delivery lane deleted it: `card` and `mentionedUserIds` are now resolved
   * from the event's own card inside the claim, and `authorisingUserId` from
   * the registration's grant. So `card: null` means what this block says it
   * means — *the product could not resolve it for this delivery* — and no
   * longer doubles as "nobody has written the resolution yet". A reader who
   * greps `unresolvedDeliveryContext` and finds only prose has learnt that the
   * TODO was closed, which is what that name was left here to tell them.
   */
  context: webhookDeliveryContextSchema,
});
export type WebhookDeliveryPayload = z.infer<typeof webhookDeliveryPayloadSchema>;
